import type { ReactNode } from 'react';
import type { ArchStatus, IssueStatus, Priority, ViolationStatus } from '@shared/types';
import { BrandMark } from './Brand';

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return <span className="spinner" role="status" aria-label={label} />;
}

export function LoadingPage({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="loading-page">
      <BrandMark />
      <Spinner />
      <span className="mut">{label}</span>
    </div>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="card">
      <div className="empty">
        <div style={{ marginBottom: hint ? 6 : 0 }}>{title}</div>
        {hint && <div className="mut">{hint}</div>}
      </div>
    </div>
  );
}

export function Banner({
  kind = 'info',
  children,
}: {
  kind?: 'info' | 'ok' | 'error' | 'warn';
  children: ReactNode;
}) {
  return <div className={`banner ${kind}`}>{children}</div>;
}

type TagTone = 'neutral' | 'ok' | 'bad' | 'info' | 'pend' | 'warn';

export function Tag({ tone = 'neutral', children }: { tone?: TagTone; children: ReactNode }) {
  return <span className={tone === 'neutral' ? 'tag' : `tag ${tone}`}>{children}</span>;
}

const ARCH_TONES: Record<ArchStatus, TagTone> = {
  Draft: 'neutral',
  Submitted: 'neutral',
  'In Review': 'info',
  'Info Requested': 'pend',
  Approved: 'ok',
  Denied: 'bad',
  Withdrawn: 'neutral',
};

export function ArchStatusTag({ status }: { status: ArchStatus }) {
  return <Tag tone={ARCH_TONES[status] ?? 'neutral'}>{status}</Tag>;
}

const ISSUE_TONES: Record<IssueStatus, TagTone> = {
  Open: 'pend',
  Acknowledged: 'info',
  'In Progress': 'info',
  Resolved: 'ok',
  Closed: 'neutral',
};

export function IssueStatusTag({ status }: { status: IssueStatus }) {
  return <Tag tone={ISSUE_TONES[status] ?? 'neutral'}>{status}</Tag>;
}

const PRIORITY_TONES: Record<Priority, TagTone> = {
  Low: 'neutral',
  Normal: 'neutral',
  High: 'warn',
  Urgent: 'bad',
};

export function PriorityTag({ priority }: { priority: Priority }) {
  if (priority === 'Normal' || priority === 'Low') return null;
  return <Tag tone={PRIORITY_TONES[priority]}>{priority}</Tag>;
}

const VIOLATION_TONES: Record<ViolationStatus, TagTone> = {
  Open: 'pend',
  Acknowledged: 'info',
  'Notice sent': 'warn',
  Resolved: 'ok',
  Dismissed: 'neutral',
};

export function ViolationStatusTag({ status }: { status: ViolationStatus }) {
  return <Tag tone={VIOLATION_TONES[status] ?? 'neutral'}>{status}</Tag>;
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="label-text">{label}</span>
      {children}
      {hint && !error && (
        <span className="mut" style={{ display: 'block', marginTop: 4 }}>
          {hint}
        </span>
      )}
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}

export function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: ReactNode;
  tone?: 'ok' | 'bad' | 'warn';
}) {
  return (
    <div className="stat">
      <span className={`value${tone ? ` ${tone}` : ''}`}>{value}</span>
      <span className="label">{label}</span>
    </div>
  );
}

/**
 * Minimal Markdown renderer for newsletter previews. Deliberately small:
 * headings, bullets, bold, italics, rules. No raw HTML is ever interpreted.
 */
export function Markdown({ source }: { source: string }) {
  const blocks: ReactNode[] = [];
  const lines = source.split('\n');
  let list: string[] = [];
  let key = 0;

  const flushList = () => {
    if (list.length === 0) return;
    blocks.push(
      <ul key={`ul-${key++}`}>
        {list.map((item, index) => (
          <li key={index}>{inline(item)}</li>
        ))}
      </ul>,
    );
    list = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^\s*[-*]\s+/.test(line)) {
      list.push(line.replace(/^\s*[-*]\s+/, ''));
      continue;
    }
    flushList();

    if (!line.trim()) continue;
    if (/^###\s+/.test(line)) blocks.push(<h3 key={key++}>{inline(line.slice(4))}</h3>);
    else if (/^##\s+/.test(line)) blocks.push(<h2 key={key++}>{inline(line.slice(3))}</h2>);
    else if (/^#\s+/.test(line)) blocks.push(<h1 key={key++}>{inline(line.slice(2))}</h1>);
    else if (/^(-{3,}|_{3,}|\*{3,})$/.test(line.trim())) blocks.push(<hr key={key++} />);
    else blocks.push(<p key={key++}>{inline(line)}</p>);
  }
  flushList();

  return <div className="md">{blocks}</div>;
}

function inline(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const token = match[0];
    if (token.startsWith('**')) parts.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    else parts.push(<em key={key++}>{token.slice(1, -1)}</em>);
    lastIndex = match.index + token.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}
